<script setup>
import { RouterLink } from "vue-router";

const currentYear = new Date().getFullYear();
</script>

<template>
  <footer
    class="app-footer mt-auto flex shrink-0 border-t border-[var(--color-border)] bg-[var(--bg-primary)]"
    aria-label="Site footer"
  >
    <div
      class="app-chrome-container app-footer__inner type-chrome-caption flex flex-col items-center justify-center gap-2 text-[var(--color-text-muted)] sm:flex-row sm:justify-between"
    >
      <p class="text-center sm:whitespace-nowrap sm:text-left">
        &copy; {{ currentYear }} PermitPilott LLC. All rights reserved.
      </p>

      <div
        class="app-footer__links flex w-full max-w-full flex-wrap items-center justify-center gap-2 sm:w-auto sm:gap-3"
      >
        <nav aria-label="Social media" class="flex items-center">
          <a
            href="https://www.facebook.com/PermitPilott"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="PermitPilott on Facebook (opens in a new tab)"
            class="inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-[var(--color-surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
          >
            <svg
              aria-hidden="true"
              focusable="false"
              viewBox="0 0 666.66668 666.66717"
              class="h-5 w-5 shrink-0"
            >
              <g
                transform="matrix(1.3333333,0,0,-1.3333333,-133.33333,799.99999)"
              >
                <g transform="translate(600,350)">
                  <path
                    d="m 0,0 c 0,138.071 -111.929,250 -250,250 -138.071,0 -250,-111.929 -250,-250 0,-117.245 80.715,-215.622 189.606,-242.638 v 166.242 h -51.552 V 0 h 51.552 v 32.919 c 0,85.092 38.508,124.532 122.048,124.532 15.838,0 43.167,-3.105 54.347,-6.211 V 81.986 c -5.901,0.621 -16.149,0.932 -28.882,0.932 -40.993,0 -56.832,-15.528 -56.832,-55.9 V 0 h 81.659 l -14.028,-76.396 h -67.631 V -248.169 C -95.927,-233.218 0,-127.818 0,0"
                    fill="#0866ff"
                  />
                </g>
                <g transform="translate(447.9175,273.6036)">
                  <path
                    d="M 0,0 14.029,76.396 H -67.63 v 27.019 c 0,40.372 15.838,55.899 56.831,55.899 12.733,0 22.981,-0.31 28.882,-0.931 v 69.253 c -11.18,3.106 -38.509,6.212 -54.347,6.212 -83.539,0 -122.048,-39.441 -122.048,-124.533 V 76.396 h -51.552 V 0 h 51.552 v -166.242 c 19.343,-4.798 39.568,-7.362 60.394,-7.362 10.254,0 20.358,0.632 30.288,1.831 L -67.63,0 Z"
                    fill="#ffffff"
                  />
                </g>
              </g>
            </svg>
          </a>
          <a
            href="https://x.com/PermitPilott"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="PermitPilott on X (opens in a new tab)"
            class="inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-[var(--color-surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
          >
            <svg
              aria-hidden="true"
              focusable="false"
              viewBox="0 0 1200 1227"
              class="h-5 w-5 shrink-0"
            >
              <path
                d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z"
                fill="#ffffff"
              />
            </svg>
          </a>
          <a
            href="https://www.youtube.com/@PermitPilott"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="PermitPilott on YouTube (opens in a new tab)"
            class="inline-flex h-12 w-14 items-center justify-center rounded-full transition-colors hover:bg-[var(--color-surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
          >
            <img
              src="/brands/youtube-icon.png"
              alt=""
              aria-hidden="true"
              class="h-auto w-[52px] shrink-0"
            />
          </a>
        </nav>

        <span
          aria-hidden="true"
          class="app-footer__divider h-4 w-px bg-[var(--color-border)] max-[24rem]:hidden"
        ></span>

        <nav
          aria-label="Legal"
          class="flex max-w-full flex-wrap items-center justify-center gap-1 sm:gap-2"
        >
          <RouterLink
            to="/terms"
            class="inline-flex min-h-11 items-center rounded-sm px-1 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
          >
            Terms
          </RouterLink>
          <RouterLink
            to="/privacy"
            class="inline-flex min-h-11 items-center rounded-sm px-1 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
          >
            Privacy
          </RouterLink>
          <RouterLink
            to="/disclaimer"
            class="inline-flex min-h-11 items-center rounded-sm px-1 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
          >
            Disclaimer
          </RouterLink>
        </nav>
      </div>
    </div>
  </footer>
</template>
